import type { ReactNode } from 'react';

/**
 * Briques de tableau de bord.
 *
 * Les graphiques (barres, anneau) ont été retirés le 11/08/2026 : Valérie
 * préfère juger de leur utilité à l'usage plutôt que de les garder « au cas
 * où ». Ils restent dans l'historique Git si le besoin se confirme — un écran
 * chargé de visuels qu'on ne regarde pas coûte plus cher qu'il ne rapporte.
 *
 * Restent une valeur mise en avant, un tableau de cumuls, et le cadre commun
 * des blocs.
 */

export type Teinte = 'vert' | 'ciel' | 'violet' | 'corail' | 'jaune' | 'nuit';

/** Fond pastel + texte foncé : jamais l'inverse (règle de la charte). */
const FONDS: Record<Teinte, string> = {
  vert: 'bg-vert-fond/35 text-vert',
  ciel: 'bg-ciel/35 text-nuit',
  violet: 'bg-violet-fond/30 text-violet',
  corail: 'bg-corail-fond/30 text-corail',
  jaune: 'bg-jaune-fond/35 text-jaune',
  nuit: 'bg-nuit/10 text-nuit',
};

export function euros(v: number): string {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
}

/**
 * Indicateur de tête : un chiffre qu'on lit en trois secondes.
 *
 * Le libellé est AU-DESSUS et petit, la valeur en dessous et grande : l'œil
 * accroche le chiffre, puis lit ce qu'il représente. L'inverse oblige à lire
 * pour comprendre ce qu'on regarde.
 */
export function Indicateur({
  libelle,
  valeur,
  aide,
  teinte = 'ciel',
  onClick,
}: {
  libelle: string;
  valeur: string;
  aide?: string;
  teinte?: Teinte;
  onClick?: () => void;
}) {
  const contenu = (
    <>
      <span className="block text-sm font-medium opacity-80">{libelle}</span>
      <span className="block font-titre text-3xl font-bold">{valeur}</span>
      {aide && <span className="block text-xs opacity-70">{aide}</span>}
    </>
  );
  const classes = `block w-full rounded-lg p-4 text-left ${FONDS[teinte]}`;
  return onClick ? (
    <button type="button" onClick={onClick} className={`${classes} transition-transform active:translate-y-px`}>
      {contenu}
    </button>
  ) : (
    <div className={classes}>{contenu}</div>
  );
}

/**
 * Tableau de cumuls : un libellé, un nombre, un montant.
 *
 * Les lignes restent CLIQUABLES quand l'appelant le demande : filtrer le
 * tableau de bord depuis un cumul est ce qui distingue un écran qu'on
 * interroge d'un écran qu'on subit.
 */
export function Cumuls({
  lignes,
  format = euros,
  onChoisir,
  selection,
}: {
  lignes: Array<{ libelle: string; montant: number; nombre?: number }>;
  format?: (v: number) => string;
  onChoisir?: (libelle: string) => void;
  selection?: string | null;
}) {
  if (lignes.length === 0) return <p className="text-sm text-nuit/60">Rien à afficher.</p>;

  return (
    <ul className="text-sm">
      {lignes.map((l) => {
        const actif = selection === l.libelle;
        return (
          <li key={l.libelle} className="border-b border-nuit/5 last:border-0">
            <button
              type="button"
              disabled={!onChoisir}
              onClick={() => onChoisir?.(l.libelle)}
              className={`flex min-h-11 w-full items-center justify-between gap-3 text-left ${
                onChoisir ? 'cursor-pointer' : 'cursor-default'
              } ${actif ? 'font-semibold' : ''}`}
            >
              <span className="min-w-0 truncate text-nuit/70">
                {l.libelle}
                {l.nombre !== undefined && <span className="text-nuit/40"> ({l.nombre})</span>}
              </span>
              <span className="shrink-0 font-medium text-nuit">{format(l.montant)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Bloc de tableau de bord : un titre, une action facultative, un contenu. */
export function Bloc({
  titre,
  action,
  children,
}: {
  titre: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-nuit/10 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-titre text-sm font-bold text-nuit">{titre}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
